import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import type { FormatterOptions, FormatterTransforms } from "@textformatter/formatter";
import type { FormatterMode } from "@textformatter/formatter";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { DEFAULT_API_URL } from "../../lib/const";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth-context";
import ApiKeyRevealDialog from "./ApiKeyRevealDialog";
import { consumeGoogleReauthFromUrl, startGoogleReauth } from "../../lib/google-reauth";

type SnippetTab = "curl" | "js";

export type ApiCallExamplePayload = {
	text: string;
	mode: FormatterMode;
	output?: "html" | "text";
	options?: Partial<FormatterOptions>;
	transforms?: Partial<FormatterTransforms>;
};

type ApiCallExampleCardProps = {
	title?: string;
	payload: ApiCallExamplePayload;
	apiKeyId?: string;
	footer?: React.ReactNode;
	onCopyWithMyKeyRequest?: (input: { snippet: string; tab: SnippetTab; apiKeyId: string }) => void;
	copyWithMyKeyLoading?: boolean;
};

const API_BASE_URL = import.meta.env.VITE_API_URL || DEFAULT_API_URL;

function stripTrailingSlashes(value: string) {
	return value.replace(/\/+$/, "");
}

function buildCurlSnippet(endpoint: string, payloadJson: string) {
	return [
		`curl -X POST "${endpoint}" \\`,
		'  -H "Content-Type: application/json" \\',
		'  -H "x-api-key: YOUR_API_KEY" \\',
		"  --data-binary @- <<'JSON'",
		payloadJson,
		"JSON",
	].join("\n");
}

function buildJsSnippet(endpoint: string, payloadJson: string) {
	return [
		`const payload = ${payloadJson};`,
		"",
		`const response = await fetch("${endpoint}", {`,
		'  method: "POST",',
		"  headers: {",
		'    "Content-Type": "application/json",',
		'    "x-api-key": "YOUR_API_KEY",',
		"  },",
		"  body: JSON.stringify(payload),",
		"});",
		"",
		"const data = await response.json();",
		"console.log(data.data.output);",
		"console.log(data.meta.wordsProcessed);",
	].join("\n");
}

function getResponseShape(format: "html" | "text") {
	return JSON.stringify(
		{
			data: {
				output: format === "html" ? "<p>...</p>" : "Plain text output",
				format,
			},
			meta: {
				wordsProcessed: 42,
			},
		},
		null,
		2,
	);
}

export default function ApiCallExampleCard({
	title = "API Call Example",
	payload,
	apiKeyId,
	footer,
	onCopyWithMyKeyRequest,
	copyWithMyKeyLoading = false,
}: ApiCallExampleCardProps) {
	const { user } = useAuth();
	const canUseGoogleReveal = Boolean(user && (user.hasGoogleAuth || user.hasPasswordAuth !== true));
	const navigate = useNavigate();
	const location = useLocation();
	const [tab, setTab] = useState<SnippetTab>("curl");
	const [revealOpen, setRevealOpen] = useState(false);
	const [revealLoading, setRevealLoading] = useState(false);
	const [reauthorizedApiKeyId, setReauthorizedApiKeyId] = useState<string | null>(null);
	const endpoint = `${stripTrailingSlashes(API_BASE_URL)}/v1/format`;
	const shouldFetchKeys = Boolean(user) && !apiKeyId;
	const keysQuery = useQuery({
		queryKey: ["api-keys", "snippet"],
		queryFn: api.listApiKeys,
		enabled: shouldFetchKeys,
	});
	const resolvedApiKeyId = useMemo(() => {
		if (apiKeyId) return apiKeyId;
		const keys = keysQuery.data?.keys ?? [];
		if (!keys.length) return "";
		const freeKey = keys.find((key) => key.label === "Free key");
		return freeKey?.id ?? keys[0]?.id ?? "";
	}, [apiKeyId, keysQuery.data?.keys]);

	const { payloadJson, curlSnippet, jsSnippet, responseShape } = useMemo(() => {
		const normalizedPayload = {
			text: payload.text,
			mode: payload.mode,
			output: payload.output ?? "html",
			options: payload.options ?? {},
			transforms: payload.transforms ?? {},
		};
		const payloadJson = JSON.stringify(normalizedPayload, null, 2);
		const responseShape = getResponseShape(normalizedPayload.output);
		return {
			payloadJson,
			curlSnippet: buildCurlSnippet(endpoint, payloadJson),
			jsSnippet: buildJsSnippet(endpoint, payloadJson),
			responseShape,
		};
	}, [endpoint, payload]);

	const activeSnippet = tab === "curl" ? curlSnippet : jsSnippet;

	async function copySnippet(text: string, successMessage: string) {
		await navigator.clipboard.writeText(text);
		toast.success(successMessage);
	}

	async function handleCopyWithMyKey(password: string) {
		if (!resolvedApiKeyId) {
			toast.error("No API key found");
			return;
		}
		setRevealLoading(true);
		try {
			const revealed = await api.revealApiKey(resolvedApiKeyId, password);
			const snippetWithKey = activeSnippet.split("YOUR_API_KEY").join(revealed.value);
			await navigator.clipboard.writeText(snippetWithKey);
			toast.success("Snippet with your API key copied");
			setRevealOpen(false);
		} catch (err) {
			const message = err instanceof Error ? err.message : "Failed to reveal API key";
			if (message === "google re-auth required") {
				startGoogleReauth({
					apiBaseUrl: stripTrailingSlashes(API_BASE_URL),
					returnTo: `${location.pathname}${location.search}`,
					storageKey: "snippet:google-reauth-action",
					action: { kind: "copy_snippet", apiKeyId: resolvedApiKeyId },
				});
				setRevealOpen(false);
				return;
			}
			throw err;
		} finally {
			setRevealLoading(false);
		}
	}

	const usesParentRevealFlow = Boolean(onCopyWithMyKeyRequest);
	const effectiveCopyWithMyKeyLoading = usesParentRevealFlow ? copyWithMyKeyLoading : revealLoading;

	useEffect(() => {
		if (usesParentRevealFlow) return;
		const result = consumeGoogleReauthFromUrl<{ kind: "copy_snippet"; apiKeyId: string }>({
			search: location.search,
			pathname: location.pathname,
			navigate,
			storageKey: "snippet:google-reauth-action",
		});
		if (!result) return;
		if (result.status === "success" && !result.action) {
			return;
		}
		if (result.status === "success" && result.action?.kind === "copy_snippet" && result.action.apiKeyId) {
			setReauthorizedApiKeyId(result.action.apiKeyId);
			toast.success("Google verification successful. Click Copy with my key again.");
			return;
		}
		if (result.status === "account_mismatch") {
			toast.error("Use the same Google account linked to this Text Formatter account.");
			return;
		}
		toast.error("Google verification failed. Please try again.");
	}, [location.pathname, location.search, navigate, usesParentRevealFlow]);

	return (
		<Card>
			{usesParentRevealFlow ? null : (
				<ApiKeyRevealDialog
					open={revealOpen}
					onOpenChange={setRevealOpen}
					onConfirm={handleCopyWithMyKey}
					onGoogleVerify={
						canUseGoogleReveal
							? () => {
									if (!resolvedApiKeyId) return;
									startGoogleReauth({
										apiBaseUrl: stripTrailingSlashes(API_BASE_URL),
										returnTo: `${location.pathname}${location.search}`,
										storageKey: "snippet:google-reauth-action",
										action: { kind: "copy_snippet", apiKeyId: resolvedApiKeyId },
									});
									setRevealOpen(false);
								}
							: undefined
					}
					canUsePasswordAuth={user?.hasPasswordAuth === true}
					canUseGoogleAuth={canUseGoogleReveal}
					loading={revealLoading}
					title="Copy snippet with your API key"
					description="Enter your account password to securely insert your API key and copy the snippet."
				/>
			)}
			<CardHeader>
				<CardTitle>{title}</CardTitle>
			</CardHeader>
			<CardContent>
				<div className="mb-3 flex flex-wrap items-end justify-between gap-3">
					<Tabs
						value={tab}
						onValueChange={(value) => {
							if (value === "curl" || value === "js") {
								setTab(value);
							}
						}}
					>
						<TabsList aria-label="API snippet format">
							<TabsTrigger value="curl">cURL</TabsTrigger>
							<TabsTrigger value="js">JavaScript</TabsTrigger>
						</TabsList>
					</Tabs>
					<div className="ml-auto flex items-center gap-2">
						<Button
							variant="secondary"
							size="sm"
							onClick={() =>
								copySnippet(activeSnippet, `${tab === "curl" ? "cURL" : "JavaScript"} copied`)
							}
						>
							Copy Snippet
						</Button>
						{user ? (
							<Button
								variant="outline"
								size="sm"
								disabled={
									effectiveCopyWithMyKeyLoading ||
									shouldFetchKeys && keysQuery.isLoading ||
									!resolvedApiKeyId
								}
								onClick={() => {
									if (!resolvedApiKeyId) {
										toast.error("No API key available. Create one in dashboard.");
										return;
									}
									if (onCopyWithMyKeyRequest) {
										onCopyWithMyKeyRequest({
											snippet: activeSnippet,
											tab,
											apiKeyId: resolvedApiKeyId,
										});
										return;
									}
									if (reauthorizedApiKeyId === resolvedApiKeyId) {
										setRevealLoading(true);
										void api
											.revealApiKey(resolvedApiKeyId)
											.then(async (revealed) => {
												const snippetWithKey = activeSnippet
													.split("YOUR_API_KEY")
													.join(revealed.value);
												await navigator.clipboard.writeText(snippetWithKey);
												toast.success("Snippet with your API key copied");
												setReauthorizedApiKeyId(null);
											})
											.catch((err: unknown) => {
												const message =
													err instanceof Error
														? err.message
														: "Failed to reveal API key";
												toast.error(message);
											})
											.finally(() => setRevealLoading(false));
										return;
									}
									setRevealOpen(true);
								}}
							>
								Copy with my key
							</Button>
						) : null}
					</div>
				</div>

				<pre className="mb-4 overflow-x-auto whitespace-pre rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-5">
					{activeSnippet}
				</pre>

				<div className="mb-2 flex items-center justify-between gap-3">
					<h3 className="text-sm font-semibold text-slate-700">Response shape</h3>
					<Button
						variant="ghost"
						size="sm"
						onClick={() => copySnippet(responseShape, "Response shape copied")}
					>
						Copy Response
					</Button>
				</div>
				<pre className="overflow-x-auto whitespace-pre rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-5">
					{responseShape}
				</pre>

				<details className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
					<summary className="cursor-pointer text-sm font-semibold text-slate-700">
						Request payload
					</summary>
					<pre className="mt-3 overflow-x-auto whitespace-pre rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-5">
						{payloadJson}
					</pre>
				</details>
				{footer}
			</CardContent>
		</Card>
	);
}
